import React, { useCallback, useMemo } from 'react';

import { useFormContext } from 'react-hook-form';

import { OnePlusManyInstanceId } from 'constants/bodyBlocks';
import { StoreBodyBlock } from 'constants/enums';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import { TemplateStudioBlock } from 'state/template-studio.state.service';

import {
  buildDefaultOnePlusManyFormStoreBlock,
  buildOnePlusManyV2ItemId,
  computeNextOnePlusManyBlockId
} from 'components/TemplatesStudio/multiInstanceBlocks';
import { IOnePlusManyFormStoreBlock } from 'components/TemplatesStudio/multiInstanceBlocks/multiInstanceBlocks.types';
import { PopoverContent } from 'components/ui';

import { mapBlockToSecondarySidebar } from '../../../../utils/blockForms.utils';

import { IAddBlockSection, IAddBlockSectionDeps } from './AddBlockModal.types';
import { resolveTemplateStudioAddBlockSections } from './AddBlockModal.utils';
import { AddBlockRow } from './components/AddBlockRow';

export type TemplateStudioAddBlockModalProps = {
  onClose: () => void;
  enabledBlocks: StoreBodyBlock[];
  sections?: IAddBlockSection[];
};

export const TemplateStudioAddBlockModal: React.FC<
  TemplateStudioAddBlockModalProps
> = ({ onClose, enabledBlocks, sections: sectionsProp }) => {
  const { getValues, setValue } = useFormContext();
  const {
    resetSidebars,
    setOpenSidebarBlock,
    setOpenSidebarMultiInstanceBlockId,
    setOpenSecondarySidebarBlock,
    setScrollToSection
  } = useTemplateStudioState();

  const focusInstance = useCallback(
    (
      block: TemplateStudioBlock,
      instanceKey: OnePlusManyInstanceId,
      wireId: string
    ) => {
      resetSidebars();
      setOpenSidebarBlock(block);
      setOpenSidebarMultiInstanceBlockId(instanceKey);
      setOpenSecondarySidebarBlock(
        mapBlockToSecondarySidebar[block] ? block : undefined
      );
      setScrollToSection(buildOnePlusManyV2ItemId(wireId));
    },
    [
      resetSidebars,
      setOpenSecondarySidebarBlock,
      setOpenSidebarBlock,
      setOpenSidebarMultiInstanceBlockId,
      setScrollToSection
    ]
  );

  const handleAppendOnePlusMany = useCallback((): void => {
    const formValues = getValues() as Record<string, unknown>;
    const nextInstanceKey = computeNextOnePlusManyBlockId(formValues);
    const newBlock = buildDefaultOnePlusManyFormStoreBlock(nextInstanceKey);

    const currentStoreBlocks = (getValues('storeBlocks') ||
      []) as IOnePlusManyFormStoreBlock[];
    setValue('storeBlocks', [...currentStoreBlocks, newBlock], {
      shouldValidate: true,
      shouldDirty: true
    });

    onClose();
    focusInstance(StoreBodyBlock.onePlusMany, nextInstanceKey, newBlock.id);
  }, [focusInstance, getValues, onClose, setValue]);

  const handleAppendMultiInstance = useCallback(
    (block: StoreBodyBlock): void => {
      if (block === StoreBodyBlock.onePlusMany) {
        handleAppendOnePlusMany();
      }
    },
    [handleAppendOnePlusMany]
  );

  const deps: IAddBlockSectionDeps = useMemo(
    () => ({
      enabledBlocks,
      appendMultiInstance: handleAppendMultiInstance
    }),
    [enabledBlocks, handleAppendMultiInstance]
  );

  const sections = useMemo(
    () => sectionsProp ?? resolveTemplateStudioAddBlockSections(deps),
    [sectionsProp, deps]
  );

  return (
    <PopoverContent
      side="right"
      align="start"
      sideOffset={8}
      onCloseAutoFocus={(e) => e.preventDefault()}
      data-testid="TemplatesStudio-AddBlockModal"
      className="w-[425px] gap-0 overflow-hidden rounded-lg border bg-white p-0 shadow-xl"
    >
      <div className="space-y-0 border-b border-neutral-100 py-4 pl-6 pr-4 text-left">
        <p className="text-p-l font-semibold text-foreground">Add Block</p>
      </div>

      <div className="max-h-[min(70vh,520px)] space-y-4 overflow-y-auto px-2 py-6">
        {sections.length === 0 ? (
          <p className="text-sm text-base-mutedForeground">
            No blocks are available to add yet.
          </p>
        ) : (
          sections.map((section) => (
            <section key={section.id} className="space-y-2">
              <p className="pl-4 text-p-xs font-semibold uppercase tracking-wide !text-[color:var(--base-muted-foreground)]">
                {section.label}
              </p>
              <div className="space-y-2">
                {section.rows.map((row) => (
                  <AddBlockRow
                    key={row.id}
                    row={row}
                    dataTestId={`TemplatesStudio-AddBlockModal-${row.id}`}
                  />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </PopoverContent>
  );
};
